<template>
  <span :class="getWrapperClass" :style="attrs.style" v-bind="{ ...listeners }">
    <BasePopover v-if="showTips" overlayClassName="mts-popover-tooltip">
      <template #content>
        <template v-if="showTips === true">
          <span v-if="renderAsHtml" v-html="getValue"></span>
          <template v-else>{{ getValue }}{{ suffix }}</template>
        </template>
        <template v-else>{{ showTips === 'REMARK' ? getRemark : showTips }} </template>
      </template>

      <span v-if="renderAsHtml" v-html="getValue"></span>
      <template v-else>{{ getValue }}{{ suffix }}</template>
    </BasePopover>
    <template v-else>
      <span v-if="renderAsHtml" v-html="getValue"></span>
      <template v-else>{{ getValue }}{{ suffix }}</template>
    </template>
  </span>
</template>

<script lang="ts" setup>
  import { get } from 'lodash-es';
  import { computed, HTMLAttributes, ref, shallowRef, useAttrs, watch } from 'vue';
  import type { Option } from '@mts-fe/types';
  import { isArray, isEqualByStringify, isFunction, propTypes } from '@mts-fe/utils';
  import { BasePopover } from '@mts/components/Base';
  import { t } from '@mts/hooks/web/useI18n';
  import { concatText } from '@mts/utils';

  defineOptions({ name: 'ReadonlyText', inheritAttrs: false });

  const props = defineProps({
    value: { type: [String, Number, Array<Option['value']>] },
    api: {
      type: Function as PropType<(arg?: any) => Promise<Option[] | Recordable>>,
      default: null,
    },
    params: propTypes.any.def({}),
    resultField: propTypes.string.def(''),
    labelField: propTypes.string.def('label'),
    valueField: propTypes.string.def('value'),
    options: { type: Array<Option>, default: [] },
    showTips: { type: [Boolean, String], default: false },
    truncate: { type: [Boolean, Number], default: false },
    concatFields: { type: Array<string>, default: [] },
    concatBy: propTypes.string.def(void 0),
    multiLang: propTypes.bool.def(false),
    multipleLine: propTypes.bool.def(void 0),
    suffix: propTypes.string,
    emptyText: propTypes.string.def('-'),
  });
  const attrs = useAttrs() as HTMLAttributes;
  const listeners = computed(() => {
    const result = {};
    for (const key in attrs) {
      if (key.startsWith('on')) result[key] = attrs[key];
    }
    return result;
  });

  const getWrapperClass = computed(() => {
    const classes = ['mts-readonly-text', attrs.class];

    const { truncate } = props;
    if (truncate) classes.push('*:line-clamp-' + (typeof truncate === 'number' ? truncate : 1));
    return classes;
  });

  const renderAsHtml = ref(false);
  const transformMultiLang = (text: string, enable = true) => (enable ? t(text) : text);
  const transformMultipleText = (text: string) => {
    const enable = props.multipleLine ?? text.includes('\n');
    if (!enable) return text;
    renderAsHtml.value = true;
    return text.replace(/\n/g, '<br>');
  };

  const loading = ref(false);
  const optionsRef = shallowRef<Option[]>([]);
  const getOptionLabel = (
    options: Option[],
    value?: Option['value'],
    labelField = props.labelField,
  ) => {
    const valueText = value?.toString() ?? '';
    const text = options.find((i) => i[props.valueField] == valueText)?.[labelField] ?? valueText;
    return text
      ? transformMultiLang(transformMultipleText(text), props.multiLang)
      : props.emptyText;
  };
  const getValue = computed(() => {
    const options = optionsRef.value.length > 0 ? optionsRef.value : props.options;
    if (!isArray(props.value)) return getOptionLabel(options, props.value);

    if (props.concatFields.length && props.value.every((i) => typeof i === 'object')) {
      const getConcatText = (value: Recordable) =>
        concatText(
          props.concatFields.map((i) => get(value, i)),
          props.concatBy,
        );
      return props.value.map(getConcatText).join(' ; ') || props.emptyText;
    }
    return props.value.map((value) => getOptionLabel(options, value)).join(' , ');
  });
  const getRemark = computed(() => {
    const options = optionsRef.value.length > 0 ? optionsRef.value : props.options;
    const getRemarkValue = (value?: Option['value']) => t(getOptionLabel(options, value, 'remark'));
    return isArray(props.value)
      ? props.value.map(getRemarkValue).join(' , ')
      : getRemarkValue(props.value);
  });

  watch(
    () => props.params,
    (value, oldValue) => {
      !isEqualByStringify(value, oldValue) && fetch();
    },
    { deep: true, immediate: true },
  );

  async function fetch() {
    let { api, params, resultField } = props;
    if (!api || !isFunction(api) || loading.value) return;
    optionsRef.value = [];
    try {
      loading.value = true;
      let res = await api(params);
      if (isArray(res)) {
        optionsRef.value = [...res];
      }
      if (resultField) {
        optionsRef.value = get(res, resultField) || [];
      }
    } catch (error) {
      console.warn(error);
    } finally {
      loading.value = false;
    }
  }
</script>

<style lang="less">
  .mts-readonly-text {
    word-break: break-all;
  }
</style>
